/**
 * Feedback Form Sheet Component
 * Side sheet for creating feedback for an employee
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { StarIcon } from 'lucide-react';

interface CreateFeedbackRequest {
  employeeId: string;
  rating: number;
  comment: string;
  jobId?: string;
}

interface FeedbackFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  onSubmit: (data: CreateFeedbackRequest) => Promise<void>;
  isSubmitting: boolean;
}

interface FeedbackFormData {
  rating: number;
  comment: string;
  jobId: string;
}

export function FeedbackFormSheet({
  open,
  onOpenChange,
  employeeId,
  onSubmit,
  isSubmitting,
}: FeedbackFormSheetProps) {
  const [formData, setFormData] = useState<FeedbackFormData>({
    rating: 0,
    comment: '',
    jobId: '',
  });

  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({
        rating: 0,
        comment: '',
        jobId: '',
      });
      setErrors({});
      setHoveredRating(0);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.rating === 0) {
      newErrors.rating = 'Please select a rating';
    }

    if (!formData.comment.trim()) {
      newErrors.comment = 'Comment is required';
    } else if (formData.comment.trim().length < 10) {
      newErrors.comment = 'Comment must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: CreateFeedbackRequest = {
      employeeId,
      rating: formData.rating,
      comment: formData.comment.trim(),
    };

    if (formData.jobId.trim()) {
      submitData.jobId = formData.jobId.trim();
    }

    try {
      await onSubmit(submitData);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[540px] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>Add Feedback</SheetTitle>
            <SheetDescription>
              Provide feedback for this employee. All fields marked with * are required.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 px-6">
            {/* Rating */}
            <div className="space-y-2">
              <Label htmlFor="rating">
                Rating <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }).map((_, index) => {
                  const starValue = index + 1;
                  const isActive = starValue <= (hoveredRating || formData.rating);
                  return (
                    <button
                      key={index}
                      type="button"
                      className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      onClick={() => setFormData({ ...formData, rating: starValue })}
                      onMouseEnter={() => setHoveredRating(starValue)}
                      onMouseLeave={() => setHoveredRating(0)}
                    >
                      <StarIcon
                        className={`h-8 w-8 transition-colors ${
                          isActive
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700'
                        }`}
                      />
                    </button>
                  );
                })}
                {formData.rating > 0 && (
                  <span className="ml-2 text-sm font-medium text-muted-foreground">
                    {formData.rating}/5
                  </span>
                )}
              </div>
              {errors.rating && (
                <p className="text-sm text-destructive">{errors.rating}</p>
              )}
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="comment">
                Comment <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="comment"
                placeholder="Share your feedback about this employee's performance..."
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                rows={6}
                className={errors.comment ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Minimum 10 characters
              </p>
              {errors.comment && (
                <p className="text-sm text-destructive">{errors.comment}</p>
              )}
            </div>

            {/* Job ID (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="jobId">Related Job ID (Optional)</Label>
              <Input
                id="jobId"
                placeholder="e.g., abc123"
                value={formData.jobId}
                onChange={(e) => setFormData({ ...formData, jobId: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Link this feedback to a specific job posting
              </p>
            </div>
          </div>

          <SheetFooter className="gap-2 grid grid-cols-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
